import React, { useCallback, useEffect, useState } from 'react';
import { cn } from '../lib/utils';

const APPLE_SCRIPT_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

/**
 * Sign in with Apple (веб). Нужны REACT_APP_APPLE_CLIENT_ID и тот же Return URL в Apple Developer (часто = origin сайта).
 */
export default function AppleSignInButton({
  clientId,
  redirectURI,
  onSuccess,
  onError,
  disabled,
  label,
  className,
}) {
  const [ready, setReady] = useState(false);

  const effectiveRedirect =
    redirectURI ||
    (typeof window !== 'undefined' ? window.location.origin : '');

  useEffect(() => {
    if (!clientId || !effectiveRedirect) return undefined;

    let cancelled = false;

    function init() {
      if (cancelled || typeof window === 'undefined' || !window.AppleID?.auth) return;
      try {
        window.AppleID.auth.init({
          clientId,
          scope: 'name email',
          redirectURI: effectiveRedirect,
          state: 'mad-signin',
          usePopup: true,
        });
        setReady(true);
      } catch (e) {
        console.error('[AppleSignIn] init', e);
        setReady(false);
      }
    }

    if (window.AppleID?.auth) {
      init();
      return () => {
        cancelled = true;
      };
    }

    const existing = document.querySelector(`script[src="${APPLE_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', init);
      return () => {
        cancelled = true;
        existing.removeEventListener('load', init);
      };
    }

    const script = document.createElement('script');
    script.src = APPLE_SCRIPT_SRC;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = init;
    script.onerror = () => {
      if (!cancelled) setReady(false);
    };
    document.head.appendChild(script);

    return () => {
      cancelled = true;
    };
  }, [clientId, effectiveRedirect]);

  const handleClick = useCallback(async () => {
    if (disabled || !ready || typeof window === 'undefined' || !window.AppleID?.auth) {
      onError?.(new Error('apple_not_ready'));
      return;
    }
    try {
      const res = await window.AppleID.auth.signIn();
      const idToken = res?.authorization?.id_token;
      if (!idToken) {
        onError?.(new Error('no_token'));
        return;
      }
      onSuccess?.({ identityToken: idToken, appleUser: res.user || null });
    } catch (e) {
      const code = e?.error || e?.detail?.error;
      if (code === 'popup_closed_by_user') return;
      onError?.(e);
    }
  }, [disabled, ready, onSuccess, onError]);

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || !ready}
      className={cn(
        'flex h-11 w-full max-w-[320px] items-center justify-center gap-2 rounded-lg bg-black px-4 text-sm font-medium text-white',
        'transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50',
        className
      )}
      aria-label={label}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
      </svg>
      <span>{label}</span>
    </button>
  );
}
